import { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const ThemeModeContext = createContext({ mode: 'light', toggle: () => {} });

const buildTheme = (mode = 'light') => {
  const isDark = mode === 'dark';
  const palette = isDark
    ? {
        mode: 'dark',
        primary: { main: '#1877F2', light: '#E9F1FF' },
        secondary: { main: '#FFD76E' },
        background: { default: '#0B1220', paper: '#0E1526' },
        text: { primary: '#E6EDF7', secondary: '#9FB3C8' },
        success: { main: '#4BB543' },
        warning: { main: '#FFB347' },
        error: { main: '#FF6B6B' },
        divider: '#172137',
        action: { hover: 'rgba(24,119,242,0.08)' },
      }
    : {
        mode: 'light',
        primary: { main: '#1877F2', light: '#E9F1FF' },
        secondary: { main: '#FFD76E' },
        background: { default: '#FAFBFF', paper: '#FFFFFF' },
        text: { primary: '#222222', secondary: '#7A869A' },
        success: { main: '#4BB543' },
        warning: { main: '#FFB347' },
        error: { main: '#FF6B6B' },
        divider: '#E6E9F0',
        action: { hover: 'rgba(24,119,242,0.06)' },
      };

  return createTheme({
    palette,
    shape: { borderRadius: 10 },
    typography: {
      fontFamily:
        'Satoshi, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, "Noto Sans", sans-serif',
      h1: { fontFamily: 'Satoshi, sans-serif', fontWeight: 700, letterSpacing: '-0.01em' },
      h2: { fontFamily: 'Satoshi, sans-serif', fontWeight: 700, letterSpacing: '-0.01em' },
      h3: { fontFamily: 'Satoshi, sans-serif', fontWeight: 700, letterSpacing: '-0.01em' },
      h4: { fontFamily: 'Satoshi, sans-serif', fontWeight: 700, letterSpacing: '-0.01em' },
      h5: { fontFamily: 'Satoshi, sans-serif', fontWeight: 600 },
      h6: { fontFamily: 'Satoshi, sans-serif', fontWeight: 600 },
      button: { fontWeight: 600, textTransform: 'none' },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            textTransform: 'none',
            boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
            '&.MuiButton-containedPrimary:hover': {
              backgroundColor: '#145dbd',
              color: '#ffffff',
            },
            '&.MuiButton-outlined:hover': {
              borderColor: '#145dbd',
              backgroundColor: isDark ? 'rgba(20,93,189,0.18)' : 'rgba(20,93,189,0.08)',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 700, height: 24 },
          label: { paddingLeft: 8, paddingRight: 8 },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
            border: isDark ? '1px solid #172137' : '1px solid #E6E9F0',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
            border: isDark ? '1px solid #172137' : '1px solid #E6E9F0',
            backgroundColor: isDark ? '#0E1526' : '#FFFFFF',
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            '&:hover': { backgroundColor: palette.action.hover },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            '&:hover': {
              backgroundColor: isDark ? 'rgba(24,119,242,0.06)' : 'rgba(24,119,242,0.04)',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#0E1526' : '#FFFFFF',
            '& fieldset': { borderColor: isDark ? '#172137' : '#E6E9F0' },
            '&:hover fieldset': { borderColor: isDark ? '#233357' : '#BFD3FF' },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { color: isDark ? '#9FB3C8' : undefined },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: { backgroundColor: 'transparent' },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: { backgroundColor: isDark ? '#0E1526' : '#F9FBFF' },
          root: { borderColor: isDark ? '#172137' : '#E6E9F0' },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: isDark ? '#F3F7FF' : '#1877F2',
            textDecorationColor: isDark ? '#F3F7FF' : '#1877F2',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: { '&:hover': { backgroundColor: palette.action.hover } },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: isDark ? 'rgba(24,119,242,0.14)' : 'rgba(24,119,242,0.10)',
            },
          },
        },
      },
    },
  });
};

export const ThemeModeProvider = ({ children }) => {
  const [mode, setMode] = useState('light');

  useEffect(() => {
    const saved = localStorage.getItem('ui_mode');
    if (saved === 'dark' || saved === 'light') setMode(saved);
  }, []);

  const value = useMemo(
    () => ({
      mode,
      toggle: () => {
        setMode((prev) => {
          const next = prev === 'light' ? 'dark' : 'light';
          localStorage.setItem('ui_mode', next);
          return next;
        });
      },
    }),
    [mode]
  );

  const theme = useMemo(() => buildTheme(mode), [mode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = () => useContext(ThemeModeContext);
